/**
 * Passo de pagamento do PA (Particular ± Plano). Sem lógica de retorno.
 */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
  FormControlLabel,
} from "@mui/material";
import { Add, AttachMoney, Badge, Warning } from "@mui/icons-material";
import { FormAutoComplete, FormMultipleAutoComplete, FormTextInput } from "@/components/form";
import { useContractContext } from "@/contexts/ContractContext";
import { useHealthPlanContractContext } from "@/contexts/HealthPlanContractContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { AffiliationModal } from "@/pages/manageplans/components/AffiliationModal";
import { PostCoverage } from "@/services/fhir/coverage/PostCoverage";
import { useCoverage } from "@/services/fhir/coverage/useCoverage";
import { useOrganizationAffiliation } from "@/services/fhir/organizationaffiliation/useOrganizationAffiliation";
import { useFormatter } from "@/utils/useFormatter";
import type { OrganizationAffiliation } from "fhir/r5";
import type { ProntoAtendimentoConfig } from "./types";
import type { ProntoSelectedPatient } from "./ProntoAtendimentoPatientStep";

export interface ProntoPaymentResult {
  appointmentIsFree: boolean;
  serviceCost: string;
  supportingInformation?: { value: string; label: string };
  procedures?: unknown[];
}

interface ProntoAtendimentoPaymentStepProps {
  config: ProntoAtendimentoConfig;
  patient: ProntoSelectedPatient;
  onConfirm: (result: ProntoPaymentResult) => void;
  loading?: boolean;
}

export function ProntoAtendimentoPaymentStep({
  config,
  patient,
  onConfirm,
  loading,
}: ProntoAtendimentoPaymentStepProps) {
  const { extractNumericValue, formatCurrency } = useFormatter;
  const { currentContract } = useContractContext();
  const { contractHealthPlan } = useHealthPlanContractContext();
  const { organization, loading: loadingOrganization } = useOrganizationContext();
  const {
    organizationAffiliation,
    loading: loadingAffiliation,
    fetchOrganizationAffiliation,
  } = useOrganizationAffiliation();
  const { coverage, fetchCoverage, loading: loadingCoverage } = useCoverage();

  const hasSubscriptionFluxPay = Boolean(
    currentContract?.[0]?.resource?.identifier?.some(
      (coding: { system?: string; value?: string }) =>
        coding.system?.includes("contract-type") &&
        coding.value === "fluxPayPlan",
    ),
  );

  const defaultCost = formatCurrency((config.value || 0) * 100);
  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      serviceCost: defaultCost,
      appointmentIsFree: false,
      supportingInformation: undefined as
        | { value: string; label: string; procedures?: unknown; defaultCardNumber?: string }
        | undefined,
      procedures: [] as unknown[],
      cardNumber: "",
    },
  });

  const appointmentIsFree = watch("appointmentIsFree");
  const healthInsuranceWatch = watch("supportingInformation");
  const cardNumber = watch("cardNumber");
  const [tabValue, setTabValue] = useState(0);
  const [showAffiliationModal, setShowAffiliationModal] = useState(false);

  const organizationAffiliationOptions = useMemo(() => {
    return organizationAffiliation
      ?.filter((affiliation) => affiliation.active === undefined || affiliation.active === true)
      ?.map((affiliation: OrganizationAffiliation) => {
        const insurancePlanExtension = affiliation.extension?.find(
          (ext) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation",
        );
        const defaultCardNumber = coverage?.find((option) => {
          return (
            insurancePlanExtension?.valueReference?.reference ===
            `InsurancePlan/${option.insurancePlan?.reference?.split("/")[1]}`
          );
        })?.identifier?.[0]?.value;
        return {
          value: affiliation.id || "",
          label: insurancePlanExtension?.valueReference?.display || "",
          procedures: affiliation.specialty,
          defaultCardNumber,
        };
      });
  }, [organizationAffiliation, coverage]);

  useEffect(() => {
    const patientId = patient.reference?.split("/")?.[1];
    if (organization?.id && patientId && tabValue === 1) {
      void fetchOrganizationAffiliation({
        primaryOrganization: organization.id,
        count: 50,
      });
      void fetchCoverage({ patient: patientId });
    }
  }, [organization?.id, patient.reference, tabValue, fetchOrganizationAffiliation, fetchCoverage, loadingOrganization]);

  const procedureOptions =
    healthInsuranceWatch?.procedures?.map((spec: { coding?: Array<{ display?: string; code?: string }>; extension?: Array<{ valueMoney?: { value?: number } }> }) => ({
      label: spec.coding?.[0]?.display || "",
      value: spec.coding?.[0]?.code || "",
      price: spec.extension?.[0]?.valueMoney?.value || 0,
    })) || [];

  const onSubmit = handleSubmit(async (data) => {
    const isFree = Boolean(data.appointmentIsFree) && tabValue === 0;
    if (tabValue === 0 && !isFree) {
      const numericValue = Number(extractNumericValue(data.serviceCost));
      if (hasSubscriptionFluxPay) {
        if (numericValue < config.value) {
          return;
        }
      } else if (numericValue < 5) {
        return;
      }
    }
    if (tabValue === 1 && !data.supportingInformation) return;

    if (tabValue === 1 && data.supportingInformation) {
      const selectedAffiliation = organizationAffiliation.find(
        (aff) => aff.id === data.supportingInformation?.value,
      );
      if (selectedAffiliation && !data.supportingInformation.defaultCardNumber) {
        try {
          const insurancePlanExtension = selectedAffiliation.extension?.find(
            (ext) =>
              ext.url ===
              "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation",
          );
          await PostCoverage(
            {
              cardNumber: data.cardNumber,
              organization: {
                value:
                  selectedAffiliation.participatingOrganization?.reference?.split("/")[1] || "",
                label: selectedAffiliation.participatingOrganization?.display || "",
              },
              insurancePlan: {
                value:
                  insurancePlanExtension?.valueReference?.reference?.split("/")[1] || "",
                label: insurancePlanExtension?.valueReference?.display || "",
                display: insurancePlanExtension?.valueReference?.display,
              },
            },
            { patientId: patient.cpf || patient.reference.split("-").slice(1).join("-") },
          );
        } catch {
          // Agenda não bloqueia o agendamento se Coverage falhar
        }
      }
    }

    const selectedAffiliation = organizationAffiliation.find(
      (aff) => aff.id === data.supportingInformation?.value,
    );
    const proceduresWithValues = (data.procedures || []).map((proc: { value?: string }) => {
      const specialty = selectedAffiliation?.specialty?.find(
        (spec) => spec.coding?.[0]?.code === proc.value,
      );
      return {
        ...proc,
        price: specialty?.extension?.[0]?.valueMoney?.value || 0,
        currency: specialty?.extension?.[0]?.valueMoney?.currency || "BRL",
      };
    });

    onConfirm({
      appointmentIsFree: isFree,
      serviceCost: isFree ? "" : data.serviceCost,
      ...(tabValue === 1 && data.supportingInformation
        ? {
            supportingInformation: {
              value: data.supportingInformation.value,
              label: data.supportingInformation.label,
            },
            procedures: proceduresWithValues,
          }
        : {}),
    });
  });

  return (
    <Stack spacing={2}>
      {contractHealthPlan && (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={(_e, next) => {
              setValue("supportingInformation", undefined);
              setTabValue(next);
            }}
          >
            <Tab label="Particular" />
            <Tab label="Plano de saúde" />
          </Tabs>
        </Box>
      )}

      {tabValue === 0 && (
        <Stack direction="row" spacing={2} alignItems="center">
          <FormTextInput
            control={control}
            label=""
            name="serviceCost"
            placeholder="R$ 0,00"
            mask={formatCurrency}
            disabled={appointmentIsFree}
            validate={(value: string) => {
              if (appointmentIsFree) return true;
              if (!hasSubscriptionFluxPay && (!value || value === "" || value === "R$ 0,00")) {
                return true;
              }
              const numericValue = Number(extractNumericValue(value));
              if (hasSubscriptionFluxPay) {
                if (numericValue < config.value) {
                  return `O valor mínimo é ${defaultCost}`;
                }
              } else if (numericValue < 5) {
                return "O valor mínimo é R$ 5,00";
              }
              return true;
            }}
          />
          {!hasSubscriptionFluxPay && (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(appointmentIsFree)}
                  onChange={(_e, checked) => setValue("appointmentIsFree", checked)}
                />
              }
              label="Cortesia"
            />
          )}
        </Stack>
      )}

      {tabValue === 1 && (
        <Stack spacing={2}>
          {loadingAffiliation || loadingCoverage ? (
            <Stack spacing={1}>
              <Skeleton variant="text" height={80} />
              <Skeleton variant="text" height={80} />
            </Stack>
          ) : organizationAffiliationOptions?.length ? (
            <FormAutoComplete
              name="supportingInformation"
              control={control}
              label="Plano de saúde"
              required
              selectOptions={organizationAffiliationOptions}
              loading={loadingAffiliation || loadingCoverage}
              renderOption={(option: { label: string; defaultCardNumber?: string }) => (
                <Stack direction="row" gap={1} alignItems="center" width="100%" justifyContent="space-between">
                  <Typography variant="body1">{option.label}</Typography>
                  {option.defaultCardNumber && (
                    <Stack direction="row" alignItems="flex-end" gap={1}>
                      <Badge />
                      <Typography variant="body1">{option.defaultCardNumber}</Typography>
                    </Stack>
                  )}
                </Stack>
              )}
              auxOnChange={(option: { defaultCardNumber?: string } | null) => {
                setValue("cardNumber", option?.defaultCardNumber || "");
              }}
            />
          ) : (
            <Stack spacing={2} sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Warning sx={{ color: "warning.main" }} />
                <Typography variant="body1">
                  Você não possui nenhum plano de saúde cadastrado ou ativo.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" startIcon={<Add />} onClick={() => setShowAffiliationModal(true)}>
                  Novo plano de saúde
                </Button>
                <Button variant="outlined" startIcon={<AttachMoney />} onClick={() => setTabValue(0)}>
                  Atendimento particular
                </Button>
              </Stack>
            </Stack>
          )}

          {healthInsuranceWatch && (
            <>
              <FormMultipleAutoComplete
                variant="filled"
                name="procedures"
                control={control}
                label="Procedimentos"
                required
                selectOptions={procedureOptions}
                loading={loadingAffiliation}
              />
              <FormTextInput
                name="cardNumber"
                defaultValue=""
                control={control}
                label="Número da carteirinha"
                required
              />
            </>
          )}
        </Stack>
      )}

      <Button
        variant="contained"
        onClick={() => void onSubmit()}
        disabled={loading || (tabValue === 1 && !healthInsuranceWatch && !cardNumber)}
        sx={{ textTransform: "none", alignSelf: "flex-end" }}
      >
        {loading ? "Confirmando..." : "Confirmar atendimento"}
      </Button>

      <AffiliationModal
        open={showAffiliationModal}
        isEditing={false}
        onClose={() => setShowAffiliationModal(false)}
        handleRefetch={async () => {
          if (organization?.id) {
            await fetchOrganizationAffiliation({
              primaryOrganization: organization.id,
              count: 50,
            });
            setShowAffiliationModal(false);
          }
        }}
      />
    </Stack>
  );
}
